import React, { useEffect, useRef, useState } from 'react';
import { 
  FaCode, 
  FaLanguage, 
  FaDatabase, 
  FaServer, 
  FaCogs,
  FaReact,
  FaNodeJs,
  FaPhp,
  FaJs,
  FaGlobe,
  FaTools,
  FaRocket,
  FaStar
} from 'react-icons/fa';
import AnimatedIcon from './AnimatedIcon';
import AnimatedCard from './AnimatedCard';

const Skills = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const technicalSkills = [
    { name: 'PHP development', level: 90, icon: FaPhp, color: 'text-purple-500' },
    { name: 'RESTful API integration', level: 85, icon: FaServer, color: 'text-green-500' },
    { name: 'Database management', level: 80, icon: FaDatabase, color: 'text-blue-500' },
    { name: 'Full stack development', level: 85, icon: FaCogs, color: 'text-orange-500' },
    { name: 'Application maintenance', level: 88, icon: FaTools, color: 'text-red-500' },
    { name: 'Software design principles', level: 75, icon: FaRocket, color: 'text-indigo-500' },
    { name: 'Node.js', level: 70, icon: FaNodeJs, color: 'text-green-400' },
    { name: 'JavaScript(ES6)', level: 80, icon: FaJs, color: 'text-yellow-500' },
    { name: 'React JS', level: 75, icon: FaReact, color: 'text-cyan-500' },
    { name: 'MongoDB', level: 70, icon: FaDatabase, color: 'text-green-600' }
  ];

  const languages = [
    { name: 'English', level: 85, icon: FaGlobe, color: 'text-blue-400' },
    { name: 'Tamil', level: 95, icon: FaStar, color: 'text-yellow-400' }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    const currentRef = sectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const SkillBar = ({ skill, index }) => (
    <AnimatedCard 
      delay={index * 100}
      animation="fadeInUp"
      hoverEffect="glow"
      glowColor="pink"
      className="mb-4 bg-gradient-to-r from-purple-900/40 via-pink-900/30 to-indigo-900/40 p-4 rounded-lg backdrop-blur-sm border border-pink-500/20"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-3">
          <AnimatedIcon 
            icon={skill.icon}
            size={20}
            color={skill.color}
            animation="none"
            delay={index * 50}
          />
          <span className="text-sm font-medium text-white">{skill.name}</span>
        </div>
        <span className="text-sm text-pink-400 font-bold">{skill.level}%</span>
      </div>
      <div className="w-full bg-purple-900/30 rounded-full h-3 overflow-hidden border border-purple-500/20">
        <div 
          className={`bg-gradient-to-r from-pink-500 via-purple-500 to-yellow-400 h-3 rounded-full transition-all duration-1000 ease-out relative ${
            isVisible ? 'animate-scale-in' : 'w-0'
          }`}
          style={{ 
            width: isVisible ? `${skill.level}%` : '0%',
            transitionDelay: `${index * 0.1}s`
          }}
        >
          {/* Animated shine effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-gradient-x"></div>
        </div>
      </div>
    </AnimatedCard>
  );

  return (
    <section ref={sectionRef} className="bg-gradient-to-b from-[#1f1f1f] via-[#2d1b3d] to-[#0e0e2c] text-white py-16 px-6 border-b-4 border-purple-500/30">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <AnimatedIcon 
            icon={FaRocket}
            size={32}
            color="text-pink-500"
            animation="bounce"
            delay={100}
            className="mx-auto mb-4"


            
          />
          <p className="text-pink-500 font-medium italic mb-2 animate-fade-in">My Expertise</p>
          <h2 className="text-2xl md:text-3xl font-bold mb-4 inline-block border-b-4 border-transparent hover:border-pink-600 pb-1 transition-colors duration-300 animate-fade-in-up">
            Technical Skills
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto italic animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            Specialized skills developed through hands-on experience in web development and logistics operations
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Technical Skills */}
          <AnimatedCard 
            animation="slideInLeft"
            delay={200}
            hoverEffect="lift"
            className="bg-gradient-to-br from-purple-900/50 via-pink-900/40 to-indigo-900/50 p-6 rounded-xl backdrop-blur-sm border border-purple-500/30"
          >
            <div className="flex items-center mb-6">
              <AnimatedIcon 
                icon={FaCode}
                size={28}
                color="text-pink-500"
                animation="glow"
                delay={250}
                className="mr-3"
              />
              <h3 className="text-xl font-bold">Technical Skills</h3>
            </div>
            <div className="space-y-4">
              {technicalSkills.map((skill, index) => (
                <SkillBar key={index} skill={skill} index={index} />
              ))}
            </div>
          </AnimatedCard>

          {/* Languages & Contact Info */}
          <div className="space-y-6">
            <AnimatedCard 
              animation="fadeInRight"
              delay={300}
              hoverEffect="lift"
              className="bg-gradient-to-br from-purple-900/50 via-pink-900/40 to-indigo-900/50 p-6 rounded-xl backdrop-blur-sm border border-purple-500/30"
            >
              <div className="flex items-center mb-6">
                <AnimatedIcon 
                  icon={FaLanguage}
                  size={28}
                  color="text-pink-500"
                  animation="rotate"
                  delay={350}
                  className="mr-3"
                />
                <h3 className="text-xl font-bold">Languages</h3>
              </div>
              <div className="space-y-4">
                {languages.map((language, index) => (
                  <SkillBar key={index} skill={language} index={index + technicalSkills.length} />
                ))}
              </div>
            </AnimatedCard>

            {/* Contact Summary */}
            <AnimatedCard 
              animation="scaleIn"
              delay={400}
              hoverEffect="glow"
              glowColor="yellow"
              className="bg-gradient-to-br from-yellow-900/60 via-orange-900/50 to-pink-900/60 p-6 rounded-xl backdrop-blur-sm border border-yellow-500/40"
            >
              <div className="flex items-center mb-4">
                <AnimatedIcon 
                  icon={FaStar}
                  size={24}
                  color="text-yellow-400"
                  animation="pulse"
                  delay={450}
                  className="mr-3"
                />
                <h4 className="text-lg font-bold text-yellow-400">Contact Summary</h4>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex items-center hover:text-pink-400 transition-colors group">
                  <span className="text-pink-500 mr-3 group-hover:animate-bounce">📧</span>
                  <span>sabarisandeveloper@gmail.com</span>
                </div>
                <div className="flex items-center hover:text-pink-400 transition-colors group">
                  <span className="text-pink-500 mr-3 group-hover:animate-bounce">📱</span>
                  <span>+91 6383767287</span>
                </div>
                <div className="flex items-center hover:text-pink-400 transition-colors group">
                  <span className="text-pink-500 mr-3 group-hover:animate-bounce">📍</span>
                  <span>Ashok Nagar, Chennai, India</span>
                </div>
              </div>
            </AnimatedCard>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Skills;